import z4 from "zod/v4";
import type { $ZodErrorTree } from "zod/v4/core";
import { getToTarget } from "../../to/use-to-target";
import { type FollowUpPageAction, type FollowUpPageState } from "./actions";
import { parseMomentFollowUpChannel } from "./parse";
import { followUpSchema, type FollowUpSchemaType } from "./schema";
import { switchChannel } from "./switch-channel";

export function followUpReducer(
  state: FollowUpPageState,
  action: FollowUpPageAction,
): FollowUpPageState {
  let newUI = state.ui;

  switch (action.type) {
    case "LOAD_START":
      return {
        ...state,
        momentDetails: { data: null, loading: true, error: null },
      };

    case "LOAD_SUCCESS":
      return {
        ...state,
        momentDetails: { data: action.payload, loading: false, error: null },
        ui: {
          ...state.ui,
          title: action.payload.momentFollowUpDetails.title,
          ...parseMomentFollowUpChannel(action.payload.momentFollowUpDetails),
        },
        errors: {
          display: state.errors.display,
          errors: validateFollowUp({
            ...state.ui,
            title: action.payload.momentFollowUpDetails.program?.title ?? state.ui.title,
          }),
        },
        derivedData: {
          toTarget: getToTarget({
            target_channel: action.payload.momentFollowUpDetails.target_channel ?? undefined,
            target_manager: action.payload.momentFollowUpDetails.target_manager ?? undefined,
            only_additional_recipients:
              action.payload.momentFollowUpDetails.only_additional_recipients ?? undefined,
            notify_channel: action.payload.momentFollowUpDetails.notify_channel ?? undefined,
            target_connection_type: action.payload.momentFollowUpDetails.target_connection_type,
          }),
        },
      };

    case "LOAD_ERROR":
      return {
        ...state,
        momentDetails: { data: null, loading: false, error: action.error },
      };

    case "SET_TITLE":
      newUI = { ...newUI, title: action.payload };
      break;

    case "SET_REASON":
      newUI = { ...newUI, reason: action.payload };
      break;

    case "SET_CHANNEL":
      newUI = {
        ...newUI,
        ...switchChannel(
          state.ui.channel,
          action.payload,
          state.ui.design,
          state.momentDetails.data?.slackVideoEnabled ?? false,
        ),
      };
      break;

    case "SET_SCHEDULE":
      newUI = { ...newUI, ...action.payload };
      break;

    case "SET_MESSAGE_DESIGN":
      newUI = { ...newUI, design: action.payload };
      break;
  }

  return {
    ...state,
    ui: newUI,
    errors: {
      display: state.errors.display,
      errors: validateFollowUp(newUI),
    },
  };
}

function validateFollowUp(data: FollowUpSchemaType): $ZodErrorTree<FollowUpSchemaType> | null {
  const { success, error } = followUpSchema.safeParse(data);
  if (success) return null;
  return z4.treeifyError(error);
}

export function makeDefaultFollowUpState(): FollowUpPageState {
  const defaultUi: FollowUpPageState["ui"] = {
    mode: "scheduled" as const,
    title: "",
    reason: null,
    channel: "work_email" as const,
    channelData: { email_sender: "", email_subject: "", email_design_id: null },
    design: null,
    schedule: {
      type: "scheduled",
      amount: null,
      period: null,
      time: null,
      timezone: null,
      allow_weekends: false,
    },
  };

  return {
    momentDetails: { data: null, loading: true, error: null },
    ui: defaultUi,
    saveState: { saving: false, error: null },
    errors: {
      display: false,
      errors: validateFollowUp(defaultUi),
    },
    derivedData: {
      toTarget: { toTarget: "employee", target_connection_type: null, isManagerConnection: false },
    },
  };
}
